import { useEffect, useRef, useState } from "react";
import { Box, Chip, Divider, FormControl, Grid, InputLabel, MenuItem, Select, Typography } from "@mui/material";
import { Add, Delete, FolderOpenOutlined } from "@mui/icons-material";
import Button from "@mui/material/Button";
import { toast } from "react-toastify";
import { translate } from "../../../../../translate/translate";
import { BaseService } from "../../../../../services/base-service";
import { URLS } from "../../../../../services/app-urls";
import { Client } from "../../../../../models/basic/client";
import { NpiChecklist } from "../../../../../models/npi/npi-checklist";
import GenericDialog from "../../../../../components/dialog/generic-dialog.component";
import { ErrorComponent } from "../../../../../components/error/error.component";
import NewNpiChecklistDialogComponent from "./new-npi-checklist-dialog.component";

import { readStoredFilters, writeStoredFilters } from "./npi-dashboard.component";

const FILTERS_STORAGE_KEY = "npi_checklist_filters";

import NpiChecklistOverviewComponent from "./npi-checklist-overview.component";
import { outlinedFieldHover, selectedChip, unselectedChip } from "../styles/npi-dashboard.styles";
import NpiChecklistScheduleComponent from "./npi-checklist-schedule.component";
import NpiChecklistLegendComponent from "./npi-checklist-legend.component";

const NpiChecklistComponent = () => {
  const storedFilters = readStoredFilters(FILTERS_STORAGE_KEY);

  const [clients, setClients] = useState<Client[]>([]);
  const [years, setYears] = useState<number[]>([]);
  const [checklists, setChecklists] = useState<NpiChecklist[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<number | "">(storedFilters?.clientId ?? "");
  const [selectedYear, setSelectedYear] = useState<number | "">(storedFilters?.year ?? "");
  const [selectedChecklistId, setSelectedChecklistId] = useState<number | null>(null);
  const [checklistToDelete, setChecklistToDelete] = useState<number | null>(null);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<"general" | "schedule">("general");
  const [refresh, setRefresh] = useState<number>(0);
  const [deleting, setDeleting] = useState<boolean>(false);
  const isDeletingRef = useRef<boolean>(false);

  const fetchClients = () => {
    const clientService = new BaseService<Client>(URLS.CLIENT);
    clientService.clearParameters();
    clientService.addParameter("active", "true");
    clientService.getAll().then((response) => {
      setClients(response.data);
      setSelectedClientId((current) =>
        response.data.some((client: Client) => client.id === current) ? current : (response.data[0]?.id ?? ""),
      );
    });
  };

  const fetchYearsInUse = () => {
    const checklistService = new BaseService<any>(URLS.NPI_CHECKLIST);
    checklistService.getAllFromListRoute("years-in-use").then((response: any) => {
      const yearOptions = response.data.length ? response.data : [new Date().getFullYear()];
      setYears(yearOptions);
      setSelectedYear((current) => (yearOptions.includes(current) ? current : yearOptions[0]));
    });
  };

  const fetchChecklists = () => {
    if (!selectedClientId || !selectedYear) {
      setChecklists([]);
      return;
    }
    const checklistService = new BaseService<NpiChecklist>(URLS.NPI_CHECKLIST);
    checklistService.clearParameters();
    checklistService.addParameter("active", "true");
    checklistService.addParameter("client", selectedClientId);
    checklistService.addParameter("year", selectedYear);
    checklistService.getAll().then((response) => {
      setChecklists(response.data);
      setSelectedChecklistId((current) =>
        response.data.some((checklist) => checklist.id === current) ? current : (response.data[0]?.id ?? null),
      );
    });
  };

  useEffect(() => {
    writeStoredFilters(FILTERS_STORAGE_KEY, {
      clientId: selectedClientId,
      year: selectedYear,
    });
  }, [selectedClientId, selectedYear]);

  useEffect(() => {
    fetchClients();
    fetchYearsInUse();
  }, []);

  useEffect(() => {
    fetchChecklists();
  }, [selectedClientId, selectedYear, refresh]);

  const handleChecklistCreated = (checklist: NpiChecklist, clientId: number) => {
    setSelectedChecklistId(checklist.id ?? null);
    setSelectedClientId(clientId);
    setSelectedYear(new Date().getFullYear());
    fetchYearsInUse();
    setRefresh((current) => current + 1);
  };

  const handleConfirmDelete = () => {
    if (isDeletingRef.current) {
      return;
    }
    isDeletingRef.current = true;
    setDeleting(true);
    const checklistService = new BaseService<NpiChecklist>(URLS.NPI_CHECKLIST);
    checklistService.delete(checklistToDelete!).then(() => {
      toast.success(translate.t("successfully_deleted"));
      setChecklistToDelete(null);
      fetchYearsInUse();
      fetchChecklists();
    }).catch((error: any) => {
      ErrorComponent(error);
    }).finally(() => {
      isDeletingRef.current = false;
      setDeleting(false);
    });
  };

  return (
    <section className="npi-dashboard-page">
      <div
        className="floating-title"
        style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center", gap: 16 }}>
        <Typography variant="h5" noWrap title={translate.t("npi_product_checklist")}
          sx={{ fontWeight: "700 !important", fontSize: { xs: "16px", md: "20px" }, letterSpacing: "0.15px", color: "#45485F", minWidth: 0, flexShrink: 1 }}>
          {translate.t("npi_product_checklist")}
        </Typography>
        <Grid container spacing={{ xs: 1, md: 2 }} alignItems="center" wrap="nowrap" width="auto" sx={{ flexShrink: 0 }}>
          <Grid>
            <FormControl size="small" sx={{ width: { xs: 140, md: 180, lg: 220 }, ...outlinedFieldHover("4px") }}>
              <InputLabel id="checklist-client-label" shrink>{translate.t("client")}</InputLabel>
              <Select
                labelId="checklist-client-label"
                label={translate.t("client")}
                notched
                displayEmpty
                sx={{ fontSize: "14px" }}
                value={selectedClientId}
                onChange={(event) => setSelectedClientId(event.target.value as number)}>
                {clients.map((client) => (
                  <MenuItem key={client.id} value={client.id}>
                    {client.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid>
            <FormControl size="small" sx={{ minWidth: 100, ...outlinedFieldHover("4px") }}>
              <InputLabel id="checklist-year-label" shrink>{translate.t("year")}</InputLabel>
              <Select
                labelId="checklist-year-label"
                label={translate.t("year")}
                notched
                displayEmpty
                sx={{ fontSize: "14px" }}
                value={selectedYear}
                onChange={(event) => setSelectedYear(event.target.value as number)}>
                {years.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setOpenDialog(true)}
              aria-label={translate.t("npi_checklist")}
              title={translate.t("npi_checklist")}
              sx={{
                backgroundColor: "#003FFB",
                whiteSpace: "nowrap",
                "&:hover": { backgroundColor: "#003FFB", opacity: 0.9 },
                minWidth: { xs: 40, md: 64 },
                px: { xs: 1, md: 2 },
                "& .MuiButton-startIcon": { mr: { xs: 0, md: 1 }, ml: { xs: 0, md: "-4px" } },
              }}>
              <Box component="span" sx={{ display: { xs: "none", md: "inline" } }}>
                {translate.t("npi_checklist").toUpperCase()}
              </Box>
            </Button>
          </Grid>
        </Grid>
      </div>
      <Box
        sx={{
          backgroundColor: "#fff",
          ml: "-15px",
          width: "calc(100% + 15px)",
          px: "15px",
          pt: 2,
          pb: 0,
        }}>
        <Grid container alignItems="center" spacing={1} sx={{ mb: 2 }}>
          {checklists.map((checklist) => {
            const isSelected = selectedChecklistId === checklist.id;
            const backgroundColor = isSelected ? "#EEF3FF" : "#FFFFFF";
            const border = isSelected ? "2px solid #295FFF" : "1px solid #E2E8F0";
            return (
              <Grid key={checklist.id}>
                <Chip
                  label={checklist.project_name}
                  onClick={() => setSelectedChecklistId(checklist.id!)}
                  onDelete={() => setChecklistToDelete(checklist.id!)}
                  deleteIcon={<Delete />}
                  sx={{
                    height: 35,
                    borderRadius: "8px",
                    fontSize: "16px",
                    fontWeight: 600,
                    color: "#1E1E1E",
                    backgroundColor,
                    border,
                    "&:hover, &.Mui-focusVisible": { backgroundColor },
                    "& .MuiChip-label": { paddingLeft: "20px", paddingRight: "12px" },
                    "& .MuiChip-deleteIcon": { color: "#606369", marginRight: "16px", "&:hover": { color: "#45485F" } },
                  }}
                />
              </Grid>
            );
          })}
        </Grid>
        <Divider sx={{ mx: "-15px", width: "calc(100% + 30px)", borderBottom: "2px solid #E2E8F0" }} />
      </Box>
      <Box sx={{ pt: 3, pr: "15px" }}>
        {selectedChecklistId ? (
          <>
            <Grid container spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <Grid>
                <Chip label={`1. ${translate.t("general")}`} 
                onClick={() => setActiveTab("general")}
                sx={{ ...(activeTab === "general" ? selectedChip : unselectedChip), fontSize: "16px" }} />
              </Grid>
              <Grid>
                <Chip label={`2. ${translate.t("checklist_schedule")}`} 
                onClick={() => setActiveTab("schedule")}
                sx={{ ...(activeTab === "schedule" ? selectedChip : unselectedChip), fontSize: "16px" }} />
              </Grid>
              {activeTab === "schedule" && (
                <Grid sx={{ ml: "auto" }}>
                  <NpiChecklistLegendComponent />
                </Grid>
              )}
            </Grid>
            {activeTab === "general" && <NpiChecklistOverviewComponent checklistId={selectedChecklistId} />}
            {activeTab === "schedule" && <NpiChecklistScheduleComponent checklistId={selectedChecklistId} />}
          </>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              gap: 1,
              px: 3,
              py: 8,
            }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 64,
                height: 64,
                borderRadius: "50%",
                backgroundColor: "#F1F5F9",
                mb: 1,
              }}>
              <FolderOpenOutlined sx={{ fontSize: 32, color: "#94A3B8" }} />
            </Box>
            <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#334155" }}>
              {translate.t("checklist_empty_title")}
            </Typography>
            <Typography sx={{ fontSize: "14px", color: "#83868C", maxWidth: 420 }}>
              {translate.t("no_checklist_found")}
            </Typography>
          </Box>
        )}
      </Box>
      <NewNpiChecklistDialogComponent open={openDialog} onClose={() => setOpenDialog(false)} onCreated={handleChecklistCreated} />
      <GenericDialog
        maxWidth="xs"
        open={checklistToDelete !== null}
        onClose={() => !deleting && setChecklistToDelete(null)}
        title={"delete"}
        colorButton={"primary"}
        labelButton={"delete"}
        closeButtonText={"cancel"}
        loading={deleting}
        disableClose
        handleConfirm={handleConfirmDelete}>
        <Typography>{translate.t("confirm_delete")}</Typography>
      </GenericDialog>
    </section>
  );
};

export default NpiChecklistComponent;
